import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CalendarDots, Clock, User, Phone, Envelope, Note } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    service_id: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    appointment_date: '',
    appointment_time: '',
    notes: ''
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to load services');
    }
  };

  // Filter time slots based on selected date and business hours
  const getAvailableTimeSlots = () => {
    if (!formData.appointment_date) return [];
    
    const selectedDate = new Date(formData.appointment_date + 'T00:00:00');
    const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 6 = Saturday
    
    const allTimeSlots = [
      '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
      '12:00 PM', '12:30 PM', '01:00 PM', '01:30 PM', '02:00 PM', '02:30 PM',
      '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM', '05:00 PM', '05:30 PM',
      '06:00 PM', '06:30 PM'
    ];
    
    // Sunday: 12pm - 5pm
    if (dayOfWeek === 0) {
      return allTimeSlots.filter(time => {
        const hour = parseInt(time.split(':')[0]);
        const isPM = time.includes('PM');
        const hour24 = isPM && hour !== 12 ? hour + 12 : (hour === 12 && !isPM ? 0 : hour);
        return hour24 >= 12 && hour24 < 17;
      });
    }
    
    // Monday-Saturday: 10am - 7pm
    return allTimeSlots.filter(time => {
      const hour = parseInt(time.split(':')[0]);
      const isPM = time.includes('PM');
      const hour24 = isPM && hour !== 12 ? hour + 12 : (hour === 12 && !isPM ? 0 : hour);
      return hour24 >= 10 && hour24 < 19;
    });
  };

  const handleNext = () => {
    if (step === 1 && !formData.service_id) {
      toast.error('Please select a service');
      return;
    }
    if (step === 2 && (!formData.appointment_date || !formData.appointment_time)) {
      toast.error('Please select date and time');
      return;
    }
    if (step === 3) {
      // Validate all required fields
      if (!formData.customer_name.trim()) {
        toast.error('Please enter your full name');
        return;
      }
      if (!formData.customer_email.trim()) {
        toast.error('Please enter your email address');
        return;
      }
      if (!formData.customer_phone.trim()) {
        toast.error('Please enter your phone number');
        return;
      }
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.customer_email)) {
        toast.error('Please enter a valid email address');
        return;
      }
      handleBooking();
      return;
    }
    setStep(step + 1);
  };

  const handleBooking = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/appointments`, formData);
      const appointment = response.data;
      
      const originUrl = window.location.origin;
      const checkoutResponse = await axios.post(`${API}/checkout/session`, {
        appointment_id: appointment.id,
        origin_url: originUrl
      });
      
      window.location.href = checkoutResponse.data.url;
    } catch (error) {
      toast.error('Failed to create appointment');
      setLoading(false);
    }
  };

  const handleDemoBooking = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/appointments`, formData);
      const appointment = response.data;
      
      toast.success('Demo booking created successfully!');
      
      setTimeout(() => {
        navigate(`/booking-success?demo=true&appointment_id=${appointment.id}`);
      }, 1000);
    } catch (error) {
      toast.error('Failed to create appointment');
      setLoading(false);
    }
  };

  const selectedService = services.find(s => s.id === formData.service_id);
  const availableTimeSlots = getAvailableTimeSlots();

  // Get minimum date (today)
  const today = new Date();
  const minDate = today.toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-background py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <Button
          data-testid="back-to-home-btn"
          variant="ghost"
          onClick={() => navigate('/demos/beauty-salon')}
          className="mb-8 gap-2"
        >
          <ArrowLeft size={20} weight="light" />
          Back to Home
        </Button>

        <div className="bg-background-paper rounded-md border border-border p-8 md:p-12">
          <div className="mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      step >= s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {s}
                  </div>
                  {s < 3 && <div className={`w-12 h-0.5 mx-2 ${ step > s ? 'bg-primary' : 'bg-muted'}`}></div>}
                </div>
              ))}
            </div>
            <h2 className="text-3xl font-serif font-medium text-center">
              {step === 1 && 'Select Service'}
              {step === 2 && 'Choose Date & Time'}
              {step === 3 && 'Your Information'}
            </h2>
          </div>

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {services.map((service) => (
                    <div
                      key={service.id}
                      data-testid={`service-card-${service.id}`}
                      onClick={() => setFormData({ ...formData, service_id: service.id })}
                      className={`p-6 rounded-md border cursor-pointer transition-all hover:border-accent ${
                        formData.service_id === service.id
                          ? 'border-accent bg-accent-soft'
                          : 'border-border bg-background'
                      }`}
                    >
                      <h4 className="font-serif text-xl mb-2">{service.name}</h4>
                      <p className="text-sm text-foreground-secondary mb-3">{service.description}</p>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-foreground-muted flex items-center gap-1">
                          <Clock size={16} weight="light" />
                          {service.duration} min
                        </span>
                        <span className="font-medium text-accent">${service.price.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <Label htmlFor="appointment_date" className="flex items-center gap-2 mb-2">
                    <CalendarDots size={18} weight="light" />
                    Appointment Date
                  </Label>
                  <Input
                    id="appointment_date"
                    data-testid="appointment-date-input"
                    type="date"
                    min={minDate}
                    value={formData.appointment_date}
                    onChange={(e) => setFormData({ ...formData, appointment_date: e.target.value, appointment_time: '' })}
                    className="bg-background-subtle"
                  />
                  {formData.appointment_date && (
                    <p className="text-xs text-foreground-secondary mt-2">
                      {new Date(formData.appointment_date + 'T00:00:00').getDay() === 0 
                        ? '⏰ Sunday hours: 12pm - 5pm'
                        : '⏰ Hours: 10am - 7pm'}
                    </p>
                  )}
                </div>

                {formData.appointment_date && (
                  <div>
                    <Label className="flex items-center gap-2 mb-3">
                      <Clock size={18} weight="light" />
                      Select Time
                    </Label>
                    {availableTimeSlots.length > 0 ? (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {availableTimeSlots.map((time) => (
                          <button
                            key={time}
                            data-testid={`time-slot-${time}`}
                            onClick={() => setFormData({ ...formData, appointment_time: time })}
                            className={`p-2 rounded-md border text-sm transition-all ${
                              formData.appointment_time === time
                                ? 'border-accent bg-accent text-accent-foreground'
                                : 'border-border bg-background hover:border-accent'
                            }`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-foreground-secondary py-8">No available time slots for this date.</p>
                    )}
                  </div>
                )}
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <Label htmlFor="customer_name" className="flex items-center gap-2 mb-2">
                    <User size={18} weight="light" />
                    Full Name *
                  </Label>
                  <Input
                    id="customer_name"
                    data-testid="customer-name-input"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    className="bg-background-subtle"
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="customer_email" className="flex items-center gap-2 mb-2">
                    <Envelope size={18} weight="light" />
                    Email Address *
                  </Label>
                  <Input
                    id="customer_email"
                    data-testid="customer-email-input"
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    className="bg-background-subtle"
                    placeholder="john@example.com"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="customer_phone" className="flex items-center gap-2 mb-2">
                    <Phone size={18} weight="light" />
                    Phone Number *
                  </Label>
                  <Input
                    id="customer_phone"
                    data-testid="customer-phone-input"
                    type="tel"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                    className="bg-background-subtle"
                    placeholder="(555) 123-4567"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="notes" className="flex items-center gap-2 mb-2">
                    <Note size={18} weight="light" />
                    Additional Notes (Optional)
                  </Label>
                  <Textarea
                    id="notes"
                    data-testid="appointment-notes-textarea"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="bg-background-subtle"
                    placeholder="Any special requests or concerns..."
                    rows={3}
                  />
                </div>

                {selectedService && (
                  <div className="p-6 rounded-md bg-accent-soft border border-accent">
                    <h4 className="font-medium mb-3">Booking Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-foreground-secondary">Service:</span>
                        <span className="font-medium">{selectedService.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-foreground-secondary">Date:</span>
                        <span className="font-medium">{formData.appointment_date}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-foreground-secondary">Time:</span>
                        <span className="font-medium">{formData.appointment_time}</span>
                      </div>
                      <div className="flex justify-between pt-3 border-t border-accent">
                        <span className="font-medium">Total:</span>
                        <span className="font-medium text-accent">${selectedService.price.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex items-center justify-between mt-8">
            <Button
              data-testid="booking-back-btn"
              variant="outline"
              onClick={() => setStep(Math.max(1, step - 1))}
              disabled={step === 1 || loading}
            >
              Back
            </Button>
            <div className="flex gap-2">
              {step === 3 && (
                <Button
                  data-testid="booking-demo-btn"
                  onClick={handleDemoBooking}
                  disabled={loading}
                  variant="outline"
                  className="border-accent text-accent hover:bg-accent hover:text-white"
                >
                  {loading ? 'Processing...' : 'Skip Payment (Demo)'}
                </Button>
              )}
              <Button
                data-testid="booking-next-btn"
                onClick={handleNext}
                disabled={loading}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {loading ? 'Processing...' : step === 3 ? 'Proceed to Payment' : 'Next'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;